import { Component, OnInit } from '@angular/core';
import { ProductionService } from '../../../../../production/services/production.service';
import {
    ICamparisonInfo,
    ReusableFunctions,
} from '../../../../../shared/classes/reusable-functions';
import { UserAccessService } from '../../../../../shared/services/user-access.service';
import { LargeCardService } from '../../services/large-card.service';
import { NoDataActionType } from '../../../../../shared/interfaces/no-data';
import { ProductionCharts } from '../../../../../shared/classes/production-chart-options';
import { ICardInfo, IDisplayFilters } from '../../interfaces/stanalone-components.data';
import * as Highcharts from 'highcharts';
import { CustomDashboardsService } from '../../../../services/my-limegear.service';
import { NavigationHelpService } from 'app/modules/shared/services/navigation-help.service';

@Component({
    selector: 'funded-by-loan-type-largecard',
    templateUrl: './funded-by-loan-type-lgc.component.html',
    styleUrls: ['./funded-by-loan-type-lgc.component.scss'],
})
export class FundedByLoanTypeLargecardComponent implements OnInit {
    constructor(
        private _productionService: ProductionService,
        private reusableFunctions: ReusableFunctions,
        private userAccessService: UserAccessService,
        private _largecardService: LargeCardService,
        private _customDashboardsService: CustomDashboardsService,
        private navigationHelpService: NavigationHelpService
    ) {}

    action: NoDataActionType;
    limeGearCharts: ProductionCharts.ProductionCharts;
    cardDrilldownUrl: string = '/reporting/production';
    colorOptions: string[] = [];
    productionLargeChart: ICardInfo;
    filters: IDisplayFilters;

    ngOnInit(): void {
        this.action = NoDataActionType.pageStart;
        this.productionLargeChart = {
            name: 'Funded by Loan Type',
            colorObject: {
                Conv: '#B870FF',
                Govt: '#57D785',
                'Non-Conf': '#FFDB70',
                Reverse: '#2C76A2',
                Other: '#FF9494',
            },
            urlSegment: 'funded-by-loan-type',
        };

        this._largecardService.initializeLargeCard(
            this.productionLargeChart,
            this.action,
            this.cardDrilldownUrl
        );
        this.limeGearCharts = new ProductionCharts.ProductionCharts(
            this.reusableFunctions,
            this.userAccessService,
            this.navigationHelpService
        );

        this._customDashboardsService.filterChangesSubject$.subscribe((changes) => {
            this.filters = this._largecardService.getDisplayOptionData(
                changes.displayOptions,
                changes.hierarchyfilters
            );

            this.action = changes.action;
            this.preparePage();
        });
    }

    preparePage() {
        this._productionService
            .displayLargeProductionCardByLoanType(
                this.filters.dateRange,
                this.filters.compareInfo_selectedOptions,
                22,
                this.filters.channel,
                this.filters.loanType,
                this.filters.loanPurpose,
                this.filters.hFilterJSON
            )
            .subscribe(
                (res) => {
                    if (res) {
                        let data = [];
                        if (this.filters.loanType.trim().toLowerCase() !== 'all') {
                            let loanTypes = this.filters.loanType.split(',');
                            for (let type of loanTypes) {
                                data = data.concat(
                                    res.filter((item) => item.loanTypeCategory === type.trim())
                                );
                            }
                        } else {
                            data = res;
                        }
                        this.populateProductionLargeCardLoanType(
                            data,
                            this.productionLargeChart,
                            null,
                            false
                        );
                    }
                },
                (error) => {
                    this.populateProductionLargeCardLoanType(
                        [],
                        this.productionLargeChart,
                        NoDataActionType.error,
                        false
                    );
                }
            );
    }

    populateProductionLargeCardLoanType(
        dataSet,
        cardInfo: ICardInfo,
        action: NoDataActionType,
        isSoldToInvestor: boolean
    ) {
        let data = dataSet.map((obj) => ({ ...obj }));
        let thisPeriodData = [];
        let comparePeriodData = [];
        let chartOptions: Highcharts.Options;
        let comparison: ICamparisonInfo;
        let sumUnitsThisPeriod: number;
        let sumVolumeThisPeriod: number;
        let uniquePeriods = this.reusableFunctions.getUniqueItemList(data, 'period');
        uniquePeriods = uniquePeriods.sort((a, b) => a - b);

        if (data.length > 0) {
            thisPeriodData = data.filter((item) => item.periodGroup === 'This Period');
            comparePeriodData = data.filter((item) => item.periodGroup === 'Compare Period');
            let xLabelsCategoryInfo = this.reusableFunctions.getCategories(
                thisPeriodData,
                ['year', 'period'],
                this.filters.dateRange
            );

            let chartData = this._largecardService.setChartData(
                thisPeriodData,
                comparePeriodData,
                'amount',
                true,
                'loanTypeCategory',
                'Funded',
                cardInfo.colorObject,
                this.filters.dateRange
            );
            sumVolumeThisPeriod = this.reusableFunctions.calculateSumByProperty(
                thisPeriodData,
                'amount'
            );
            sumUnitsThisPeriod = this.reusableFunctions.calculateSumByProperty(
                thisPeriodData,
                'nbr'
            );
            const sumUnitsLastPeriod = this.reusableFunctions.calculateSumByProperty(
                comparePeriodData,
                'nbr'
            );
            comparison = this.reusableFunctions.calculateComparisonPercentage(
                sumUnitsThisPeriod,
                sumUnitsLastPeriod
            );

            let processedChartSeries = this._largecardService.setAllChartSeriesOptions(
                cardInfo,
                chartData.lineData,
                chartData.stackColumnsData,
                this.filters.compareInfo_selectedOptions,
                this.cardDrilldownUrl,
                this.filters.compareInfo_selectedOptionsFriendly,
                this.filters.loanPurpose,
                this.filters.loanType,
                this.limeGearCharts
            );

            chartOptions = this.limeGearCharts.createNewStackedChart(
                cardInfo.name,
                this.reusableFunctions.getChartXLabel(this.filters.dateRange),
                'Volume ($)',
                processedChartSeries,
                true,
                'left',
                this.cardDrilldownUrl,
                cardInfo.urlSegment,
                this.filters.compareInfo_selectedOptions,
                this.filters.compareInfo_selectedOptionsFriendly,
                xLabelsCategoryInfo[0],
                this.filters.loanPurpose,
                this.filters.loanType
            );
        }

        cardInfo.largeCardInteriorOptions = this._largecardService.updateChartData(
            cardInfo,
            chartOptions,
            data.length > 0,
            true,
            action,
            comparison,
            sumUnitsThisPeriod,
            sumVolumeThisPeriod,
            true,
            false,
            this.filters.loanPurpose,
            this.filters.loanType,
            this.filters.compareInfo_selectedOptions
        );
    }
}
